import React, { useState, useEffect } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Tooltip } from "primereact/tooltip";
import { Tag } from "primereact/tag";
import { Dropdown, DropdownChangeEvent, DropdownFilterEvent } from "primereact/dropdown";
import { getOrderDeliveryStatus } from "../../services/orders/orderservice";
import orderServices from "../../services/orders/orderservice";

import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeflex/primeflex.css";
import "primeicons/primeicons.css";

// --- INTERFACE DEFINITIONS (UPDATED FOR TYPE SAFETY) ---
interface OrderDeliveryStatusRow {
  orderID: number;
  orderDate: string;
  assignedTo: string | null;
  assignedDate: string | null;
  delayedStatus: string | null;
  orderStatus: string | null;
  deliveredDate: string | null;
  deliveredBy: string | null;
}

interface UserOption {
  userID: number;
  name: string;
  primaryContactNbr: string;
}

interface UserResult {
  userID: number;
  detail: string;
}

interface AssignmentStep {
  assignedTo: string | null;
  assignedDate: string | null;
  status: string | null;
}

interface GroupedOrder {
  orderID: number;
  orderDate: string;
  assignmentSteps: AssignmentStep[];
  deliveredDate?: string | null;
  deliveredBy?: string | null;
  orderStatus?: string | null;
}

interface Step {
  // Enforcing label as string to satisfy the component structure (Timeline label must be visible)
  label: string; 
  icon?: string;
  date?: string | null;
  status?: string | null;
  active: boolean;
  custom: boolean;
  deliveredBy?: string | null;
}

// --- COMPONENT LOGIC ---
const OrderDeliveryStatusComponent: React.FC = () => {
  const [filters, setFilters] = useState({ orderID: null as number | null, primaryContactNbr: "" as string | null });
  const [data, setData] = useState<OrderDeliveryStatusRow[]>([]);
  const [recentOrders, setRecentOrders] = useState<OrderDeliveryStatusRow[]>([]);
  const [selectedOrders, setSelectedOrders] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>();
  const blankUserDetail: UserOption[] = [{ userID: 0, name: "", primaryContactNbr: "" }];
  const [orderUsers, setOrderUsers] = useState<UserOption[]>(blankUserDetail);
  const [orderUser, setOrderUser] = useState<UserResult[]>([]);
  const [searchMode, setSearchMode] = useState<"mobile" | "orderId" | null>(null);

  useEffect(() => {
    setOrderUser(orderUsers.map((user) => ({ userID: user.userID, detail: `${user.name}, ${user.primaryContactNbr}` })));
  }, [orderUsers]);

  const handleDropdownChange = async (e: DropdownChangeEvent) => {
    setSelectedUser(e.value);
    const selectedUserData = orderUsers.find((user) => user.userID === e.value);

    if (selectedUserData) {
      setFilters({ orderID: null, primaryContactNbr: selectedUserData.primaryContactNbr });

      try {
        setLoading(true);
        setSearchMode("mobile");
        // Assuming getOrderDeliveryStatus accepts filters and returns the order data
        const result = await getOrderDeliveryStatus({ primaryContactNbr: selectedUserData.primaryContactNbr });

        const allOrders = (result.orders || []).sort(
          (a: any, b: any) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime()
        );

        setRecentOrders(allOrders);
        setSelectedOrders([]);
        setData(result.orders || []);
        setHasSearched(true);
      } catch (error) {
        console.error("Error fetching recent orders:", error);
        setRecentOrders([]);
        setData([]);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDropdownFilter = (e: DropdownFilterEvent) => {
    if (e.filter.length > 2) {
      // Assuming orderServices.getOrderUsers is defined and returns UserOption[]
      orderServices.getOrderUsers(e.filter).then((response: UserOption[]) => {
        if (response.length > 0) setOrderUsers(response);
      });
    }
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "orderID") {
      setFilters({ ...filters, orderID: value ? Number(value.replace(/[^0-9]/g, "")) : null, primaryContactNbr: null });
      setSelectedUser(null);
    }
  };

  const handleSearch = async () => {
    if (!filters.orderID && !filters.primaryContactNbr) return;

    try {
      setLoading(true);
      setHasSearched(true);
      const mode = filters.orderID ? "orderId" : "mobile";
      setSearchMode(mode);

      // Assuming getOrderDeliveryStatus accepts filters and returns the order data
      const result = await getOrderDeliveryStatus(filters);
      
      const sortedResult = (result.orders || []).sort((a: any, b: any) => {
        if (a.assignedDate === b.assignedDate) return 0;
        if (a.assignedDate === null) return 1;
        if (b.assignedDate === null) return -1;
        return new Date(a.assignedDate).getTime() - new Date(b.assignedDate).getTime();
      });

      setData(sortedResult);

      if (mode === "mobile") {
        setRecentOrders(sortedResult);
        setSelectedOrders([]);
      } else {
        const orderIds = sortedResult.map((order: OrderDeliveryStatusRow) => order.orderID);
        setSelectedOrders(orderIds);
        setRecentOrders([]);
      }
    } catch (error) {
      console.error("Error fetching order delivery data:", error);
      setData([]);
      setRecentOrders([]);
      setSelectedOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({ orderID: null, primaryContactNbr: "" });
    setSelectedUser(null);
    setOrderUsers(blankUserDetail);
    setData([]);
    setRecentOrders([]);
    setSelectedOrders([]);
    setHasSearched(false);
    setSearchMode(null);
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    // Format: Oct 15, 2025
    return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  };

  const getStatusSeverity = (status: string | null | undefined) => {
    if (!status || status.toLowerCase() === "status is not updated") return "info";
    const normalized = status.toLowerCase();
    if (normalized.includes("delivered") || normalized.includes("completed")) return "success"; 
    if (normalized.includes("cancelled") || normalized.includes("failed") || normalized.includes("not delivered"))
      return "danger";
    return "warning";
  };

  const groupedData = (() => {
    const groupedMap = new Map<number, GroupedOrder>();
    const filteredData = selectedOrders.length > 0 ? data.filter((o) => selectedOrders.includes(o.orderID)) : data;

    // Use a Map to group the rows into single orders
    filteredData.forEach((order) => {
      if (!groupedMap.has(order.orderID)) {
        groupedMap.set(order.orderID, {
          orderID: order.orderID,
          orderDate: order.orderDate,
          assignmentSteps: [],
          // Initialize final status fields to null/undefined
          deliveredDate: null, 
          deliveredBy: null,
          orderStatus: null,
        });
      }

      const groupedOrder = groupedMap.get(order.orderID)!;

      // ************ RECTIFICATION APPLIED HERE ************
      // Update the final delivery status fields if the current row has them.
      // This ensures that the 'Delivered' status overrides any 'null' status encountered first.
      if (order.orderStatus) {
        groupedOrder.orderStatus = order.orderStatus;
        groupedOrder.deliveredDate = order.deliveredDate;
        groupedOrder.deliveredBy = order.deliveredBy;
      }
      // ****************************************************

      // Only add assignment steps for records that have assignment data
      if (order.assignedTo || order.assignedDate) {
        groupedOrder.assignmentSteps.push({
          assignedTo: order.assignedTo,
          assignedDate: order.assignedDate,
          status: order.delayedStatus || "Status is not updated",
        });
      }
    });

    // Sort assignment steps by date (ascending)
    groupedMap.forEach((order) => {
      order.assignmentSteps.sort((a, b) => {
        if (!a.assignedDate) return 1;
        if (!b.assignedDate) return -1;
        return new Date(a.assignedDate).getTime() - new Date(b.assignedDate).getTime();
      });
    });

    // Return the array of unique orders
    return Array.from(groupedMap.values());
  })();

  const showTimelineDirectly = searchMode === "orderId" && data.length > 0;
  const showCheckboxes = searchMode === "mobile" && recentOrders.length > 0;
  
  // Define custom Green colors
  const PRIMARY_COLOR = 'var(--green-500)'; 
  const INACTIVE_COLOR = 'var(--surface-border)';
  const INACTIVE_DOT_BG = 'var(--surface-50)';

  return (
    <div className="p-3">
      <Card
        title={
          <div className="flex align-items-center gap-2">
            <i className="pi pi-truck text-2xl" style={{ color: PRIMARY_COLOR }}></i>
            <span className="text-2xl font-bold">Order Delivery Status</span>
          </div>
        }
        className="shadow-3 border-round-xl surface-card"
      >
        <Tooltip target=".info-tooltip" />

        {/* Filters */}
        <div className="surface-ground p-4 border-round-lg mb-4">
          <div className="flex flex-wrap align-items-end gap-3">
            <div className="flex flex-column" style={{ width: "200px" }}>
              <label htmlFor="orderID" className="block font-semibold mb-2 text-900">Order ID</label>
              <InputText
                id="orderID"
                name="orderID"
                placeholder="Order ID"
                value={filters.orderID?.toString() || ""}
                onChange={handleFilterChange}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                className="w-full"
                disabled={loading}
              />
            </div>

            <div className="flex flex-column" style={{ width: "250px" }}>
              <label htmlFor="primaryContactNbr" className="block font-semibold mb-2 text-900">
                <i className="pi pi-phone info-tooltip" data-pr-tooltip="Search by user mobile number"></i>
                Phone Number
              </label>
              <Dropdown
                id="primaryContactNbr"
                name="primaryContactNbr"
                options={orderUser}
                optionLabel="detail"
                optionValue="userID"
                value={selectedUser}
                onChange={handleDropdownChange}
                onFilter={handleDropdownFilter}
                filter
                placeholder="Search for User/Mobile"
                className="w-full"
                disabled={loading}
              />
            </div>

            <div className="flex gap-2">
              <Button
                label="Search"
                icon="pi pi-search"
                style={{ backgroundColor: PRIMARY_COLOR, borderColor: PRIMARY_COLOR }}
                className="font-semibold"
                onClick={handleSearch}
                loading={loading}
                disabled={!filters.orderID && !filters.primaryContactNbr}
                size="small"
              />
              <Button
                label="Clear"
                icon="pi pi-filter-slash"
                className="p-button-outlined p-button-secondary font-semibold"
                onClick={clearFilters}
                disabled={loading}
                size="small"
              />
            </div>
          </div>
        </div>
        
        {/* Recent Orders */}
        {showCheckboxes && (
          <div className="surface-ground p-3 border-round-lg mb-4">
            <div className="font-bold mb-2">Select Orders to Track</div>
            <div className="flex flex-column gap-2">
              {/* Ensure only unique orders are displayed in checkboxes */}
              {Array.from(new Map(recentOrders.map(o => [o.orderID, o])).values()).map((order) => (
                <div key={order.orderID} className="flex align-items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedOrders.includes(order.orderID)}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setSelectedOrders((prev) =>
                        checked ? [...prev, order.orderID] : prev.filter((id) => id !== order.orderID)
                      );
                    }}
                  />
                  <label>Order #{order.orderID} - {formatDate(order.orderDate)}</label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Orders Timeline */}
        {loading ? (
          <div className="text-center py-6">
            <i className="pi pi-spinner pi-spin text-4xl" style={{ color: PRIMARY_COLOR }}></i>
            <p className="mt-3 font-medium text-lg text-900">Loading orders...</p>
          </div>
        ) : (selectedOrders.length > 0 || showTimelineDirectly) && groupedData.length > 0 ? (
          <div className="flex flex-column gap-6 mt-4">
            {groupedData.map((order) => {
              
              const finalStatusRecord = {
                orderStatus: order.orderStatus,
                deliveredDate: order.deliveredDate,
                deliveredBy: order.deliveredBy,
              };

              // Determine the final state label and active status based on the requirement
              const isStatusNull = finalStatusRecord.orderStatus === null || finalStatusRecord.orderStatus === undefined;
              
              // FIX: Ensure finalLabel is a non-null string
              // If status is null, show "Not Delivered", otherwise show the status (or "Final Status" if status is undefined/empty)
              const finalLabel = isStatusNull 
                ? "Not Delivered" 
                : finalStatusRecord.orderStatus || "Final Status";
              
              // Mark as active if the status is NOT null, regardless of the status value (Delivered, Cancelled, etc.)
              const isActiveFinalStep = !isStatusNull;

              // Rebuilding the steps based on the dynamic assignment data
              const steps: Step[] = [
                {
                  label: "Order Placed",
                  icon: "pi pi-shopping-cart",
                  date: order.orderDate,
                  active: true,
                  custom: false
                },
                ...order.assignmentSteps.map((assignment, index) => ({
                  label: `Assignment ${index + 1}:\n${assignment.assignedTo || 'N/A'}`, // Ensure this is a string
                  // *** MODIFICATION HERE: Changed icon to fixed "pi pi-truck" ***
                  icon: "pi pi-truck", 
                  date: assignment.assignedDate,
                  status: assignment.status || "Status is not updated",
                  active: !!assignment.assignedDate,
                  custom: true,
                })),
                {
                  label: finalLabel, // Now guaranteed to be a string
                  // Use appropriate icon based on the status value (Cancelled gets the red X icon)
                  icon: finalLabel.toLowerCase().includes("cancelled") 
                    ? "pi pi-times-circle" 
                    : finalLabel.toLowerCase().includes("delivered") 
                    ? "pi pi-check-circle" 
                    : "pi pi-clock", // Default icon for Not Delivered or other pending status
                  date: finalStatusRecord.deliveredDate,
                  status: finalStatusRecord.orderStatus,
                  active: isActiveFinalStep, // Now driven by whether orderStatus is NULL
                  custom: false,
                  deliveredBy: finalStatusRecord.deliveredBy, 
                },
              ];

              const completedStepsCount = steps.filter(step => step.active).length;
              const totalSteps = steps.length;
              const progressPercentage = totalSteps > 1 ? ((completedStepsCount - 1) / (totalSteps - 1)) * 100 : 0;
              
              // Also ensure the card background color reflects the final status
              const cardBgColor =
                order.orderStatus?.toLowerCase() === "cancelled"
                  ? "var(--red-50)"
                  : order.orderStatus?.toLowerCase() === "delivered"
                    ? "var(--green-50)" 
                    : "var(--surface-card)";

              return (
                <div key={order.orderID} className="surface-card p-5 border-round-xl shadow-2" style={{ backgroundColor: cardBgColor }}>
                  <div className="mb-4 font-bold text-xl" style={{ color: PRIMARY_COLOR }}>ORDER #{order.orderID}</div>

                  {/* Horizontal Progress Bar Structure */}
                  <div className="relative mb-6" style={{ height: '80px' }}>
                    
                    {/* The Horizontal Line/Progress Bar Wrapper - Centered */}
                    <div className="absolute top-0 left-0 w-full" style={{ top: '15px', padding: '0 15px', height: '10px' }}>
                        {/* Background Line */}
                        <div className="bg-gray-300" style={{ height: '6px', width: '100%', position: 'absolute', top: '2px', borderRadius: '3px', backgroundColor: 'var(--green-100)' }}></div>
                        {/* Progress Fill Line */}
                        <div
                            style={{
                              height: '6px',
                              width: `${progressPercentage}%`,
                              position: 'absolute',
                              top: '2px',
                              left: 0,
                              transition: 'width 0.5s ease-in-out',
                              backgroundColor: PRIMARY_COLOR, 
                              borderRadius: '3px',
                            }}
                        ></div>
                    </div>

                    <div className="flex justify-content-between align-items-start position-relative" style={{ zIndex: 5 }}>
                      {steps.map((step, index) => {
                        const isFirst = index === 0; 
                        const isActiveStep = step.active;
                        const isLast = index === steps.length - 1;
                        
                        // Check if the final step is Cancelled to apply a red dot border
                        const isCancelled = isLast && finalLabel.toLowerCase().includes("cancelled");
                        
                        const dotBgColor = isActiveStep ? PRIMARY_COLOR : INACTIVE_DOT_BG; 
                        // If Cancelled, use a red border for the dot
                        const dotBorderColor = isCancelled ? 'var(--red-500)' : (isActiveStep ? PRIMARY_COLOR : INACTIVE_COLOR); 
                        
                        // If Cancelled, change the active dot color to red for better visual cue
                        const finalDotBgColor = isCancelled ? 'var(--red-500)' : dotBgColor;


                        return (
                          <div 
                            key={index} 
                            className="flex flex-column align-items-center"
                            style={{ 
                              flex: isFirst || isLast ? "0 0 auto" : "1 1 0%", 
                              minWidth: '100px', 
                              maxWidth: '200px',
                            }}
                          >
                            
                            {/* The DOT with Checkmark/Empty Circle */}
                            <div
                                className="border-circle mx-auto flex justify-content-center align-items-center p-overlay-badge"
                                style={{
                                  width: "30px",
                                  height: "30px",
                                  backgroundColor: finalDotBgColor, // Use custom background for cancelled
                                  border: `2px solid ${dotBorderColor}`, // Use custom border for cancelled
                                  color: 'white', 
                                  fontSize: '1rem',
                                  marginTop: '-12px', 
                                  transition: 'all 0.3s ease',
                                }}
                              >
                                {isActiveStep ? (
                                  <i className={step.icon}></i> // Use step's icon for the final active step (incl. pi-times-circle for cancelled)
                                  ) : (
                                    <div style={{ 
                                        width: '10px', 
                                        height: '10px', 
                                        borderRadius: '50%', 
                                        border: `2px solid ${INACTIVE_COLOR}`,
                                        backgroundColor: INACTIVE_DOT_BG,
                                      }} />
                                  )}
                              </div>
                            
                            {/* Labels (Below the line) */}
                            <div className="text-center mt-3" style={{ maxWidth: '120px', minHeight: '60px' }}>
                                <div className="font-semibold text-900 text-sm mb-1 whitespace-pre-line">{step.label.replace(': ', ':\n')}</div>
                                {/* Inner wrapper for status tag and date to manage stacking */}
                                <div className="flex flex-column align-items-center gap-1 mt-1">
                                    {step.custom && (
                                        <Tag
                                            value={step.status || "Status is not updated"}
                                            severity={getStatusSeverity(step.status)}
                                            className="px-2 py-1 border-round-sm font-medium text-xs"
                                            style={{ lineHeight: 1 }}
                                        />
                                    )}
                                    {/* Display Delivered By for the final step */}
                                    {isLast && step.deliveredBy && (
                                        <div className="text-800 font-medium text-xs">BY: {step.deliveredBy}</div>
                                    )}
                                    <div className="text-600 text-xs">{formatDate(step.date)}</div> 
                                </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                </div>
              );
            })}
          </div>
        ) : hasSearched ? (
          <div className="text-center py-6">

          </div>
        ) : (
          <div className="text-center py-6">
            <i className="pi pi-search text-4xl text-400 mb-3"></i>
            <p className="text-900 font-medium text-lg">Start your search</p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default OrderDeliveryStatusComponent;
